import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, IconButton,
  Paper, Stack, Tab, Table, TableBody, TableCell, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MiniMarkdown from '../components/MiniMarkdown';
import AlphabetTable from '../components/AlphabetTable';
import { hasCyrillic, toLatin } from '../russianLetters';
import { useLang } from '../i18n';
import {
  createPersonalItem, deletePersonalItem, getCurrentCourse, getPersonalItems, getStudySettings, updateStudySettings,
} from '../api';

// под русским текстом — латиница (для итальянцев, изучающих русский)
function Latin({ text, show }) {
  if (!show || !hasCyrillic(text)) return null;
  return (
    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontStyle: 'italic' }} lang="it">
      {toLatin(text)}
    </Typography>
  );
}

// rows: [[изучаемый, ...остальные колонки]]
function RowsTable({ rows, translit }) {
  return (
    <Table size="small">
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.join('|')}>
            <TableCell sx={{ py: 0.5, width: '45%' }}>
              <strong>{row[0]}</strong>
              <Latin text={row[0]} show={translit} />
            </TableCell>
            {row.slice(1).map((cell, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableCell key={i} sx={{ py: 0.5 }}>
                {cell}
                <Latin text={cell} show={translit} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Section({ title, rows, translit = false }) {
  if (!rows?.length) return null;
  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      <RowsTable rows={rows} translit={translit} />
    </Box>
  );
}

function CourseTab() {
  const { t } = useLang();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(
    () => getCurrentCourse().then(setCourse).catch(() => setError(t('course.loadError'))), [t],
  );
  useEffect(() => { load(); }, [load]);

  const navigate = useNavigate();
  // урок становится текущим (его карточки пойдут первыми) — и сразу к занятию
  const startLesson = async (n) => {
    await updateStudySettings({ petrov_lesson: n });
    navigate(`/study?lesson=${n}`);
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!course) return <CircularProgress />;
  if (!course.key) return <Typography color="text.secondary">{t('course.noCourse')}</Typography>;
  const ru = course.target === 'ru';
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('course.intro')}</Typography>
      {course.lessons.map((l) => (
        <Accordion key={l.n} disableGutters defaultExpanded={l.n === course.petrov_lesson}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={{ opacity: l.unlocked ? 1 : 0.6 }}>{l.n}. {l.title}</Typography>
              <Chip size="small" label={l.level} variant="outlined" />
              {l.n === course.petrov_lesson && <Chip size="small" color="primary" label={t('course.current')} />}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <MiniMarkdown text={l.grammar} translit={ru && l.n > 0} />
            <AlphabetTable rows={l.alphabet} />
            <Section title={t('course.reading')} rows={l.reading} />
            {l.verbs.length > 0 && (
              <Box sx={{ my: 2 }}>
                <Typography variant="subtitle2">{t('course.conjugations', { tenses: l.tense_labels.join(', ') })}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {l.verbs.map(([inf, gloss]) => (ru ? `${inf} [${toLatin(inf)}] (${gloss})` : `${inf} (${gloss})`)).join(', ')}
                </Typography>
              </Box>
            )}
            <Section title={t('course.words')} rows={l.vocab} translit={ru} />
            <Section title={t('course.phrases')} rows={l.phrases} translit={ru} />
            <Section title={t('course.drills')} rows={(l.drills || []).map(([prompt, answer]) => [prompt, answer])} translit={ru} />
            {l.n > course.petrov_lesson && (
              <Button variant="contained" onClick={() => startLesson(l.n)}>{t('course.startLesson')}</Button>
            )}
            {l.n === course.petrov_lesson && (
              <Button variant="contained" component={RouterLink} to={`/study?lesson=${l.n}`}>{t('course.studyNow')}</Button>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function PersonalTab() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [target, setTarget] = useState('it');
  const [form, setForm] = useState({ target: '', native: '', note: '' });
  const [error, setError] = useState('');
  const load = useCallback(
    () => getPersonalItems().then(setItems).catch(() => setError(t('course.loadDictError'))), [t],
  );
  useEffect(() => {
    load();
    getStudySettings().then((s) => setTarget(s.target || 'it')).catch(() => {});
  }, [load]);

  // В БД поля буквальные: it — итальянский текст, ru — русский
  const toPayload = () => (target === 'ru'
    ? { ru: form.target, it: form.native, note: form.note || null }
    : { it: form.target, ru: form.native, note: form.note || null });

  const add = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createPersonalItem(toPayload());
      setForm({ target: '', native: '', note: '' });
      load();
    } catch (err) {
      setError(err.response?.status === 409 ? t('course.duplicate') : t('course.addError'));
    }
  };

  const remove = async (id) => {
    await deletePersonalItem(id);
    load();
  };

  return (
    <Box>
      <Paper component="form" onSubmit={add} sx={{ p: 2, mb: 2 }}>
        <Stack spacing={1.5}>
          <TextField label={t(`course.target.${target}`)} value={form.target} required
            onChange={(e) => setForm({ ...form, target: e.target.value })}
            inputProps={{ autoCapitalize: 'none', spellCheck: false, lang: target }} />
          <TextField label={t(`course.native.${target}`)} value={form.native} required
            onChange={(e) => setForm({ ...form, native: e.target.value })} />
          <TextField label={t('course.note')} value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <Button type="submit" variant="contained">{t('course.add')}</Button>
        </Stack>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!items ? <CircularProgress /> : (
        <Table size="small">
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id}>
                <TableCell sx={{ py: 0.5 }}>
                  <strong>{target === 'ru' ? i.ru : i.it}</strong>
                  {i.note && <Typography variant="caption" color="text.secondary" component="div">{i.note}</Typography>}
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>{target === 'ru' ? i.it : i.ru}</TableCell>
                <TableCell sx={{ py: 0.5, width: 40 }}>
                  <IconButton size="small" onClick={() => remove(i.id)} aria-label={t('course.delete')}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

export default function Course() {
  const { lang, t } = useLang();
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 2 }}>
      <Typography variant="h4" gutterBottom>{t('course.title')}</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={t('course.lessons')} />
        <Tab label={t('course.mine')} />
      </Tabs>
      {tab === 0 ? <CourseTab /> : <PersonalTab />}
      {lang === 'ru' && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 4, textAlign: 'center' }}>
          {t('course.oldMode')} <RouterLink to="/cards">{t('course.oldCards')}</RouterLink> ·{' '}
          <RouterLink to="/words">{t('course.oldWords')}</RouterLink>
        </Typography>
      )}
    </Box>
  );
}
